import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapPin, Building2, Users, GraduationCap, Globe, ExternalLink, Eye, Phone, ChevronDown, Search, Filter, RefreshCw, Database, Briefcase, TrendingUp, MapPinned, UserCheck } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';

/* ─── Fix default Leaflet markers ─── */
// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/* ─── Our school marker ─── */
function createSchoolIcon(color: string) {
  return L.divIcon({
    className: 'school-marker',
    html: `<div style="
      width:40px;height:40px;border-radius:50%;
      background:${color};border:3px solid white;
      box-shadow:0 3px 12px rgba(0,0,0,.35);
      display:flex;align-items:center;justify-content:center;
      color:white;font-weight:800;font-size:16px;
      position:relative;z-index:900;
    ">🏫</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
}

/* ─── Fallback coords ─── */
const FALLBACK_COORDS: [number, number][] = [
  [-19.9050, -43.9550], [-19.9320, -43.9400],
  [-19.9191, -43.9386], [-19.9450, -43.9200],
];

const UF_BOUNDS: Record<string, { lat: [number, number]; lng: [number, number]; nome: string }> = {
  AC: { lat: [-11.15, -7.11], lng: [-74.01, -66.57], nome: 'Acre' },
  AL: { lat: [-10.50, -8.81], lng: [-38.24, -35.45], nome: 'Alagoas' },
  AM: { lat: [-9.82, 2.25], lng: [-73.79, -56.10], nome: 'Amazonas' },
  AP: { lat: [-1.24, 4.44], lng: [-52.91, -49.87], nome: 'Amapa' },
  BA: { lat: [-18.35, -8.53], lng: [-46.62, -37.34], nome: 'Bahia' },
  CE: { lat: [-7.86, -2.78], lng: [-41.42, -37.25], nome: 'Ceara' },
  DF: { lat: [-16.05, -15.50], lng: [-48.28, -47.31], nome: 'Distrito Federal' },
  ES: { lat: [-21.30, -17.89], lng: [-41.88, -39.66], nome: 'Espirito Santo' },
  GO: { lat: [-19.50, -12.39], lng: [-53.25, -45.91], nome: 'Goias' },
  MA: { lat: [-10.26, -1.04], lng: [-48.75, -41.80], nome: 'Maranhao' },
  MG: { lat: [-22.92, -14.23], lng: [-51.05, -39.86], nome: 'Minas Gerais' },
  MS: { lat: [-24.07, -17.17], lng: [-57.65, -50.92], nome: 'Mato Grosso do Sul' },
  MT: { lat: [-18.04, -7.35], lng: [-61.63, -50.22], nome: 'Mato Grosso' },
  PA: { lat: [-9.84, 2.60], lng: [-58.90, -46.06], nome: 'Para' },
  PB: { lat: [-8.31, -6.02], lng: [-38.77, -34.79], nome: 'Paraiba' },
  PE: { lat: [-9.48, -7.33], lng: [-41.36, -34.86], nome: 'Pernambuco' },
  PI: { lat: [-10.93, -2.74], lng: [-45.99, -40.37], nome: 'Piaui' },
  PR: { lat: [-26.72, -22.52], lng: [-54.62, -48.02], nome: 'Parana' },
  RJ: { lat: [-23.37, -20.76], lng: [-44.89, -40.96], nome: 'Rio de Janeiro' },
  RN: { lat: [-6.98, -4.83], lng: [-37.25, -34.95], nome: 'Rio Grande do Norte' },
  RO: { lat: [-13.69, -7.97], lng: [-66.62, -59.77], nome: 'Rondonia' },
  RR: { lat: [-1.59, 5.27], lng: [-64.83, -58.88], nome: 'Roraima' },
  RS: { lat: [-33.75, -27.08], lng: [-57.65, -49.69], nome: 'Rio Grande do Sul' },
  SC: { lat: [-29.39, -25.96], lng: [-53.84, -48.55], nome: 'Santa Catarina' },
  SE: { lat: [-11.57, -9.51], lng: [-38.24, -36.39], nome: 'Sergipe' },
  SP: { lat: [-25.31, -19.78], lng: [-53.11, -44.16], nome: 'Sao Paulo' },
  TO: { lat: [-13.47, -5.17], lng: [-50.73, -45.73], nome: 'Tocantins' },
};

const REDE_COLORS: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b', FEDERAL: '#3b82f6' };

/* ─── Types ─── */
interface Escola {
  id: string; nome: string; codigo: string; cidade?: string; uf?: string;
  totalAlunos?: number; tipoRede?: string; latitude?: number | null; longitude?: number | null;
  municipio?: { nome: string; estado?: { nome: string; sigla: string } };
  _count?: { alunos: number; turmas: number };
}

interface ClusterData {
  id: number; latitude: number; longitude: number; contagem: number;
  uf: string | null; municipio: string | null;
  municipal: number; estadual: number; federal: number; privada: number;
}

interface CatalogoEscola {
  id: number; codigoInep: string; nome: string; municipio: string;
  dependenciaAdministrativa: string; categoriaAdministrativa: string;
  porteEscola?: string; telefone?: string;
  latitude: number; longitude: number;
}

/* ─── Helpers ─── */
function formatCount(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(1).replace('.0', '')}K`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace('.0', '')}K`;
  return `${n}`;
}

const DEP_COLORS: Record<string, string> = { municipal: '#10b981', estadual: '#8b5cf6', federal: '#3b82f6', privada: '#f59e0b' };

// Cluster color: gray (no our schools) or orange (has our schools)
function getClusterColor(hasOurSchools: boolean, filterDep?: string): string {
  if (filterDep) return DEP_COLORS[filterDep.toLowerCase()] || '#94a3b8';
  return hasOurSchools ? '#f97316' : '#475569'; // orange vs slate-600
}
function getClusterBorderColor(hasOurSchools: boolean, filterDep?: string): string {
  if (filterDep) return DEP_COLORS[filterDep.toLowerCase()] || '#94a3b8';
  return hasOurSchools ? '#ea580c' : '#334155';
}

function getDominantColor(c: ClusterData, filterDep?: string): string {
  if (filterDep) return DEP_COLORS[filterDep.toLowerCase()] || '#94a3b8';
  const max = Math.max(c.municipal, c.estadual, c.federal, c.privada);
  if (max === c.municipal) return '#10b981';
  if (max === c.estadual) return '#8b5cf6';
  if (max === c.federal) return '#3b82f6';
  return '#f59e0b';
}

function getClusterRadius(contagem: number, zoom: number): number {
  const base = Math.min(Math.max(Math.log2(contagem + 1) * 3.5, 8), 35);
  // Scale down at different zoom levels to prevent overlap
  if (zoom <= 4) return base * 0.9;
  if (zoom <= 5) return base * 1.0;
  if (zoom <= 7) return base * 0.75;
  if (zoom <= 9) return base * 0.65;
  return base * 0.6;
}

/** Spatially merge nearby clusters to avoid overlap. Returns merged super-clusters. */
function mergeClusters(clusters: ClusterData[], zoom: number, maxVisible: number): ClusterData[] {
  if (clusters.length <= maxVisible) return clusters;

  // Distance threshold in degrees — scales with zoom
  const threshold = zoom <= 5 ? 3 : zoom <= 7 ? 1.5 : zoom <= 9 ? 0.5 : 0.2;

  // Sort by contagem desc — largest clusters absorb smaller ones
  const sorted = [...clusters].sort((a, b) => b.contagem - a.contagem);
  const merged: ClusterData[] = [];
  const absorbed = new Set<number>();

  for (const c of sorted) {
    if (absorbed.has(c.id)) continue;

    // Find nearby clusters to absorb
    let totalContagem = c.contagem;
    let totalMun = c.municipal, totalEst = c.estadual, totalFed = c.federal, totalPriv = c.privada;
    let weightedLat = c.latitude * c.contagem, weightedLng = c.longitude * c.contagem;

    for (const other of sorted) {
      if (other.id === c.id || absorbed.has(other.id)) continue;
      const dist = Math.sqrt((c.latitude - other.latitude) ** 2 + (c.longitude - other.longitude) ** 2);
      if (dist < threshold) {
        absorbed.add(other.id);
        totalContagem += other.contagem;
        totalMun += other.municipal; totalEst += other.estadual;
        totalFed += other.federal; totalPriv += other.privada;
        weightedLat += other.latitude * other.contagem;
        weightedLng += other.longitude * other.contagem;
      }
    }

    merged.push({
      ...c,
      contagem: totalContagem,
      municipal: totalMun, estadual: totalEst, federal: totalFed, privada: totalPriv,
      latitude: weightedLat / totalContagem,
      longitude: weightedLng / totalContagem,
      // Keep uf/municipio from the largest cluster
    });

    if (merged.length >= maxVisible) break;
  }

  return merged;
}

/* ─── Map helpers ─── */
function FlyToState({ uf, flyKey }: { uf: string | null; flyKey: number }) {
  const map = useMap();
  useEffect(() => {
    if (flyKey === 0) return; // Skip initial mount
    if (!uf) {
      // Fly back to full Brazil view
      map.flyTo([-14.2, -51.9], 4, { duration: 1.2 });
    } else if (UF_BOUNDS[uf]) {
      const b = UF_BOUNDS[uf];
      map.flyToBounds(L.latLngBounds(L.latLng(b.lat[0], b.lng[0]), L.latLng(b.lat[1], b.lng[1])), { padding: [30, 30], maxZoom: 11, duration: 1.2 });
    }
  }, [uf, flyKey, map]);
  return null;
}

function MapEventTracker({ onViewChange }: { onViewChange: (bounds: L.LatLngBounds, zoom: number) => void }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => onViewChange(map.getBounds(), map.getZoom()), 200);
    return () => clearTimeout(t);
  }, [map, onViewChange]);
  useMapEvents({
    moveend() { onViewChange(map.getBounds(), map.getZoom()); },
    zoomend() { onViewChange(map.getBounds(), map.getZoom()); },
  });
  return null;
}

function ActionButton({ c, clusterMetric, currentZoom }: { c: any, clusterMetric: string, currentZoom: number }) {
  const map = useMap();
  const navigate = useNavigate();
  return (
    <button onClick={() => {
      if (clusterMetric === 'nossas') {
        navigate('/master/escolas' + (c.uf ? `?uf=${c.uf}` : ''));
      } else {
        map.flyTo([c.latitude, c.longitude], currentZoom <= 5 ? 8 : 14, { duration: 1.2 });
      }
    }} style={{
      width: '100%', padding: '8px', marginTop: 8, borderRadius: 8, fontSize: '.8rem', fontWeight: 700,
      background: '#111', color: 'white', border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      boxShadow: '0 2px 4px rgba(0,0,0,.15)'
    }}><TrendingUp size={14} /> {clusterMetric === 'nossas' ? 'Ver em Gestão' : (currentZoom <= 5 ? 'Explorar Municípios' : 'Explorar Escolas')}</button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
export function MasterMapaPage() {
  const navigate = useNavigate();
  const [escolas, setEscolas] = useState<Escola[]>([]);
  const [loading, setLoading] = useState(true);

  // Cluster state
  const [clusters, setClusters] = useState<ClusterData[]>([]);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const clusterCacheRef = useRef<Map<string, ClusterData[]>>(new Map());

  // Individual schools at high zoom
  const [detalheEscolas, setDetalheEscolas] = useState<CatalogoEscola[]>([]);

  // Cluster generation
  const [clusterStatus, setClusterStatus] = useState<{ gerado: boolean; total: number; porZoom: any[] } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genElapsed, setGenElapsed] = useState(0);
  const [genTimedOut, setGenTimedOut] = useState(false);
  const genAbortRef = useRef<AbortController | null>(null);
  const genTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Filters
  const [selectedUf, setSelectedUf] = useState<string | null>(null);
  const [flyToUf, setFlyToUf] = useState<string | null>(null);
  const [flyKey, setFlyKey] = useState(0);
  const [filterDep, setFilterDep] = useState('');
  const [searchText, setSearchText] = useState('');
  // Metric displayed inside cluster circles
  type ClusterMetric = 'escolas' | 'municipios' | 'municipal' | 'estadual' | 'federal' | 'privada' | 'nossas';
  const [clusterMetric, setClusterMetric] = useState<ClusterMetric>('escolas');

  // Intel Municipal data (funil de vendas)
  const [intelUfData, setIntelUfData] = useState<any[]>([]);
  const [intelMunCache, setIntelMunCache] = useState<Map<string, any>>(new Map());
  const [intelDetail, setIntelDetail] = useState<any>(null);

  // Start elapsed timer when generating
  useEffect(() => {
    if (generating) {
      setGenElapsed(0);
      setGenTimedOut(false);
      genTimerRef.current = setInterval(() => {
        setGenElapsed(prev => {
          const next = prev + 1;
          if (next >= 10) setGenTimedOut(true);
          return next;
        });
      }, 1000);
    } else {
      if (genTimerRef.current) { clearInterval(genTimerRef.current); genTimerRef.current = null; }
    }
    return () => { if (genTimerRef.current) clearInterval(genTimerRef.current); };
  }, [generating]);

  // Cancel cluster generation
  const cancelGenerate = useCallback(() => {
    if (genAbortRef.current) genAbortRef.current.abort();
    setGenerating(false);
    setGenElapsed(0);
    setGenTimedOut(false);
  }, []);

  // Load our schools + check cluster status + intel data
  useEffect(() => {
    Promise.all([
      api.get(ROUTES.escolas.listar).then(r => {
        const list = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
        setEscolas(list.filter((e: any) => e.ativo && !e.isDemo));
      }),
      api.get(ROUTES.catalogo.clustersStatus).then(r => {
        setClusterStatus(r.data);
        // Auto-generate clusters if not done yet
        if (!r.data.gerado) {
          setGenerating(true);
          const controller = new AbortController();
          genAbortRef.current = controller;
          api.post(ROUTES.catalogo.gerarClusters, {}, { signal: controller.signal })
            .then(gr => {
              setClusterStatus(gr.data);
              clusterCacheRef.current.clear();
            })
            .catch(() => {})
            .finally(() => setGenerating(false));
        }
      }),
      api.get(ROUTES.intelMunicipal.resumoUf).then(r => setIntelUfData(r.data || [])).catch((err) => console.warn('[MasterMapaPage] Erro ao carregar resumo UF:', err)),
    ]).catch((err) => console.warn('[MasterMapaPage] Erro no Promise.all principal do mapa:', err)).finally(() => setLoading(false));
  }, []);

  // Fetch municipality intel when popup is clicked
  const fetchMunIntel = useCallback(async (municipio: string, uf: string) => {
    const key = `${uf}|${municipio}`;
    if (intelMunCache.has(key)) {
      setIntelDetail(intelMunCache.get(key));
      return;
    }
    try {
      // First get ranking for UF to find codIbge
      const r = await api.get(ROUTES.intelMunicipal.ranking(uf));
      const items = r.data || [];
      const normalizar = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      const found = items.find((m: any) => normalizar(m.demografia?.nomeMunicipio || '') === normalizar(municipio));
      if (found) {
        const detail = await api.get(ROUTES.intelMunicipal.detalhe(found.codIbge));
        const data = detail.data;
        setIntelDetail(data);
        setIntelMunCache(prev => new Map(prev).set(key, data));
      }
    } catch (err) {
      console.warn('[MasterMapaPage] Erro ao carregar intel do município:', err);
    }
  }, [intelMunCache]);

  const getPos = useCallback((e: Escola, i: number): [number, number] => {
    if (e.latitude && e.longitude) return [e.latitude, e.longitude];
    return FALLBACK_COORDS[i % FALLBACK_COORDS.length];
  }, []);
  const positions = useMemo(() => escolas.map((e, i) => getPos(e, i)), [escolas, getPos]);

  // ── Fetch clusters for current view ──
  const fetchClusters = useCallback(async (bounds: L.LatLngBounds, zoom: number) => {
    setCurrentZoom(zoom);

    // At zoom >= 12, fetch individual points
    if (zoom >= 12) {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      try {
        const r = await api.get(ROUTES.catalogo.area, {
          params: { latMin: sw.lat, latMax: ne.lat, lngMin: sw.lng, lngMax: ne.lng, limit: 500 },
        });
        setDetalheEscolas(r.data?.data || []);
        setClusters([]);
      } catch { /* ignore */ }
      return;
    }

    setDetalheEscolas([]);

    // Map zoom to cluster level — SKIP grid Z6, go to municipality Z8 directly
    let zoomLevel: number;
    if (zoom <= 5) zoomLevel = 4;       // States (27 clusters)
    else if (zoom <= 9) zoomLevel = 8;  // Municipalities (~5,570, filtered by bounds)
    else zoomLevel = 10;                // Micro-grid

    const cacheKey = `${zoomLevel}`;

    // Check cache — only Z4 is cached entirely (others depend on bounds)
    if (zoomLevel === 4 && clusterCacheRef.current.has(cacheKey)) {
      setClusters(clusterCacheRef.current.get(cacheKey)!);
      return;
    }

    setLoadingClusters(true);
    try {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const params: Record<string, number> = { zoom: zoomLevel };
      // For Z4, don't send bounds — get all states
      if (zoomLevel > 4) {
        // Add buffer for edge clusters
        const latBuf = (ne.lat - sw.lat) * 0.3;
        const lngBuf = (ne.lng - sw.lng) * 0.3;
        params.latMin = sw.lat - latBuf;
        params.latMax = ne.lat + latBuf;
        params.lngMin = sw.lng - lngBuf;
        params.lngMax = ne.lng + lngBuf;
      }

      const r = await api.get(ROUTES.catalogo.clusters, { params });
      const data: ClusterData[] = r.data?.data || [];

      // No filtering — we render small clusters as dots instead of hiding them

      // Cache Z4 level entirely
      if (zoomLevel === 4) {
        clusterCacheRef.current.set(cacheKey, data);
      }

      setClusters(data);
    } catch { /* ignore */ }
    setLoadingClusters(false);
  }, []);

  // Pre-compute our schools per UF and per municipio
  const nossasEscolasPorUf = useMemo(() => {
    const map: Record<string, number> = {};
    escolas.forEach(e => {
      const uf = e.uf || e.municipio?.estado?.sigla;
      if (uf) map[uf] = (map[uf] || 0) + 1;
    });
    return map;
  }, [escolas]);

  const nossasEscolasPorMun = useMemo(() => {
    const map: Record<string, number> = {};
    escolas.forEach(e => {
      const mun = e.cidade || e.municipio?.nome;
      if (mun) {
        const key = mun.toLowerCase().trim();
        map[key] = (map[key] || 0) + 1;
      }
    });
    return map;
  }, [escolas]);

  // ── Apply local filter on clusters ──
  const filteredClusters = useMemo(() => {
    const isDependencia = ['municipal', 'estadual', 'federal', 'privada'].includes(clusterMetric);
    const isNossas = clusterMetric === 'nossas';
    const isStateLevel = currentZoom <= 5;
    
    if (!isDependencia && !isNossas) return clusters;

    // When filtering by dep or nossas, show only clusters with > 0 items
    return clusters.map(c => {
      let count = 0;
      if (isDependencia) {
        count = (c[clusterMetric as keyof ClusterData] as number) || 0;
      } else if (isNossas) {
        count = isStateLevel ? (c.uf ? nossasEscolasPorUf[c.uf] || 0 : 0) : (c.municipio ? nossasEscolasPorMun[c.municipio.toLowerCase().trim()] || 0 : 0);
      }
      
      if (count === 0) return null;
      return { ...c, contagem: count };
    }).filter(Boolean) as ClusterData[];
  }, [clusters, clusterMetric, currentZoom, nossasEscolasPorUf, nossasEscolasPorMun]);

  // ── Handle state selection ──
  const handleSelectUf = useCallback((uf: string) => {
    setSelectedUf(uf);
    setFlyToUf(uf);
    setFlyKey(k => k + 1);
  }, []);

  // ── Generate clusters ──
  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    const controller = new AbortController();
    genAbortRef.current = controller;
    try {
      const r = await api.post(ROUTES.catalogo.gerarClusters, {}, { signal: controller.signal });
      setClusterStatus(r.data);
      // Reload clusters for current view
      clusterCacheRef.current.clear();
    } catch { /* ignore */ }
    setGenerating(false);
  }, []);

  const totalAlunos = escolas.reduce((s, e) => s + (e._count?.alunos || e.totalAlunos || 0), 0);
  const totalTurmas = escolas.reduce((s, e) => s + (e._count?.turmas || 0), 0);
  const totalClusters = clusterStatus?.total || 0;



  // Helper: get municipality count for a UF from intel data
  const getMunCountForUf = useCallback((uf: string) => {
    const info = intelUfData.find(u => u.uf === uf);
    return info?.total_municipios || null;
  }, [intelUfData]);

  if (loading) return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid var(--border-color)', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-muted)' }}>Carregando mapa...</p>
      </div>
    </div>
  );

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ AUTO-PROCESSING BANNER (inline, no blocking dialog) ═══ */}
      {generating && (
        <div style={{
          background: genTimedOut ? 'linear-gradient(135deg, #dc2626, #991b1b)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
          borderRadius: 12, padding: '14px 20px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 14, transition: 'background .5s',
        }}>
          <div style={{ width: 22, height: 22, border: '2.5px solid rgba(255,255,255,.3)', borderTop: '2.5px solid white', borderRadius: '50%', animation: 'spin .8s linear infinite', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '.9rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              {genTimedOut ? 'Sem resposta do servidor...' : 'Processando Catalogo Nacional'}
              <span style={{ background: 'rgba(255,255,255,.2)', padding: '1px 8px', borderRadius: 6, fontSize: '.72rem', fontWeight: 600, fontFamily: 'monospace' }}>
                {genElapsed}s
              </span>
            </div>
            <div style={{ color: 'rgba(255,255,255,.7)', fontSize: '.72rem', marginTop: 2 }}>
              {genTimedOut
                ? `Aguardando ha ${genElapsed}s — o backend pode estar processando 158K registros. Voce pode cancelar e tentar novamente.`
                : `Agregando 158K escolas em 4 niveis de zoom...`
              }
            </div>
          </div>
          <button onClick={cancelGenerate} style={{
            padding: '6px 14px', borderRadius: 8, fontSize: '.78rem', fontWeight: 700,
            background: 'rgba(255,255,255,.2)', color: 'white', border: '1px solid rgba(255,255,255,.3)',
            cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background .2s',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.35)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,.2)')}
          >Cancelar</button>
        </div>
      )}

      {/* ═══ HERO ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #042f2e 0%, #064e3b 50%, #065f46 100%)',
        borderRadius: '20px 20px 0 0', padding: '20px 32px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(16,185,129,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Globe size={16} style={{ color: '#34d399' }} />
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Cobertura Territorial</span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.3rem', fontWeight: 800, margin: '0 0 14px' }}>Mapa de Cobertura</h1>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            {[
              { icon: Building2, value: escolas.length, label: 'Nossas Escolas', color: '#34d399' },
              { icon: GraduationCap, value: totalAlunos.toLocaleString('pt-BR'), label: 'Alunos Conectados', color: '#a78bfa' },
              { icon: Users, value: totalTurmas, label: 'Turmas Ativas', color: '#fbbf24' },
              { icon: Database, value: totalClusters.toLocaleString('pt-BR'), label: 'Clusters Gerados', color: '#38bdf8' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}20`, color: s.color }}>
                  <s.icon size={20} />
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ FILTERS BAR ═══ */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: '0 0 16px 16px',
        border: '1px solid var(--border-color, #e5e7eb)', borderTop: 'none',
        padding: '12px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <Filter size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <div style={{ position: 'relative' }}>
          <select value={selectedUf || ''} onChange={(e) => {
            if (e.target.value) { handleSelectUf(e.target.value); }
            else { setSelectedUf(null); setFlyToUf(null); setFlyKey(k => k + 1); }
          }}
            style={{ padding: '6px 28px 6px 10px', borderRadius: 8, fontSize: '.78rem', fontWeight: 600, border: '1px solid var(--border-color)', background: 'var(--bg-main)', color: 'var(--text-main)', cursor: 'pointer', appearance: 'none' as const, minWidth: 160 }}>
            <option value="">Todos os Estados</option>
            {Object.entries(UF_BOUNDS).sort((a, b) => a[1].nome.localeCompare(b[1].nome)).map(([uf, info]) => (
              <option key={uf} value={uf}>{info.nome} ({uf})</option>
            ))}
          </select>
          <ChevronDown size={12} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
        </div>
        {/* Dropdown de dependências removido a pedido do usuário */}
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" placeholder="Buscar por nome (zoom 13+)..." value={searchText} onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '100%', padding: '6px 10px 6px 30px', borderRadius: 8, fontSize: '.78rem', border: '1px solid var(--border-color)', background: 'var(--bg-main)', color: 'var(--text-main)' }} />
        </div>
        {clusterStatus?.gerado && (
          <button onClick={() => { handleGenerate(); }} title="Reprocessar clusters"
            style={{ padding: '5px 8px', borderRadius: 6, fontSize: '.7rem', background: 'var(--bg-main)', border: '1px solid var(--border-color)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={12} /> Reprocessar
          </button>
        )}
      </div>

      {/* ═══ METRIC TOGGLES ═══ */}
      <div style={{
        background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        borderTop: 'none', borderRadius: '0 0 12px 12px', marginTop: -16, marginBottom: 16,
        padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginRight: 4 }}>Exibir:</span>
        {([
          { key: 'escolas' as ClusterMetric, label: 'Escolas', color: '#475569' },
          { key: 'municipios' as ClusterMetric, label: 'Municípios', color: '#6366f1' },
          { key: 'municipal' as ClusterMetric, label: 'Municipal', color: '#10b981' },
          { key: 'estadual' as ClusterMetric, label: 'Estadual', color: '#8b5cf6' },
          { key: 'federal' as ClusterMetric, label: 'Federal', color: '#3b82f6' },
          { key: 'privada' as ClusterMetric, label: 'Privada', color: '#f59e0b' },
          { key: 'nossas' as ClusterMetric, label: 'Nossas Escolas', color: '#059669' },
        ]).map(m => {
          const active = clusterMetric === m.key;
          return (
            <button key={m.key} onClick={() => setClusterMetric(m.key)} style={{
              padding: '3px 10px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
              background: active ? m.color : 'var(--bg-main, #f1f5f9)',
              color: active ? 'white' : 'var(--text-muted)',
              border: active ? 'none' : '1px solid var(--border-color, #e5e7eb)',
              cursor: 'pointer', transition: 'all .15s',
            }}>{m.label}</button>
          );
        })}
      </div>

      {/* ═══ MAP ═══ */}
      <div style={{
        borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border-color, #e5e7eb)',
        boxShadow: '0 4px 24px rgba(0,0,0,.08)', height: 540, position: 'relative',
      }}>
        <MapContainer center={[-14.2, -51.9]} zoom={4} minZoom={4} style={{ height: '100%', width: '100%', zIndex: 1 }} scrollWheelZoom={true}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
          />
          <FlyToState uf={flyToUf} flyKey={flyKey} />
          <MapEventTracker onViewChange={fetchClusters} />

          {/* ── Server-side clusters — merged to avoid overlap ── */}
          {(() => {
            const MAX_VISIBLE = currentZoom <= 5 ? 30 : 15;
            const isStateLevel = currentZoom <= 5;
            // Merge at ALL zoom levels to prevent circle overlap
            const displayClusters = mergeClusters(filteredClusters, currentZoom, MAX_VISIBLE);
            return displayClusters.map((c) => {
            // Detect if we have schools
            const hasOurSchoolsUf = !!(c.uf && nossasEscolasPorUf[c.uf] > 0);
            const hasOurSchoolsMun = !!(c.municipio && nossasEscolasPorMun[c.municipio.toLowerCase().trim()] > 0);
            const hasOurSchools = isStateLevel ? hasOurSchoolsUf : hasOurSchoolsMun;
            const munCount = isStateLevel && c.uf ? getMunCountForUf(c.uf) : null;

            const tempDep = ['municipal', 'estadual', 'federal', 'privada'].includes(clusterMetric) ? clusterMetric.charAt(0).toUpperCase() + clusterMetric.slice(1) : undefined;
            const color = getClusterColor(hasOurSchools, tempDep);
            const borderColor = getClusterBorderColor(hasOurSchools, tempDep);
            const radius = getClusterRadius(c.contagem, currentZoom);
            const opacity = filterDep ? 0.85 : (hasOurSchools ? 0.85 : 0.55);
            return (
              <CircleMarker
                key={`cl-${c.id}`}
                center={[c.latitude, c.longitude]}
                radius={radius}
                pathOptions={{
                  fillColor: color,
                  fillOpacity: opacity,
                  color: borderColor,
                  weight: hasOurSchools ? 3 : 1.5,
                  opacity: 0.9,
                }}
              >
                <Tooltip direction="center" permanent className="cluster-label">
                  <span style={{ fontWeight: 800, fontSize: radius > 18 ? '13px' : '10px', color: 'white', textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>
                    {(() => {
                      if (clusterMetric === 'municipios') return isStateLevel && munCount ? munCount : formatCount(c.contagem);
                      if (clusterMetric === 'municipal') return formatCount(c.municipal);
                      if (clusterMetric === 'estadual') return formatCount(c.estadual);
                      if (clusterMetric === 'federal') return formatCount(c.federal);
                      if (clusterMetric === 'privada') return formatCount(c.privada);
                      if (clusterMetric === 'nossas') {
                        const n = isStateLevel ? (c.uf ? nossasEscolasPorUf[c.uf] || 0 : 0) : (c.municipio ? nossasEscolasPorMun[c.municipio.toLowerCase().trim()] || 0 : 0);
                        return formatCount(n);
                      }
                      // 'escolas' — always show school count (contagem)
                      return formatCount(c.contagem);
                    })()}
                  </span>
                </Tooltip>
                <Popup eventHandlers={{ add: () => {
                  // Lazy fetch intel data when popup opens
                  if (currentZoom <= 5 && c.uf) {
                    // State level - data already in intelUfData
                  } else if (c.municipio && c.uf) {
                    fetchMunIntel(c.municipio, c.uf);
                  }
                }}}>
                  <div style={{ minWidth: 260, fontFamily: 'Inter, system-ui, sans-serif' }}>
                    {/* ── Zoom Z4-5: State-level popup ── */}
                    {currentZoom <= 5 && c.uf ? (() => {
                      const ufInfo = intelUfData.find(u => u.uf === c.uf);
                      const ufName = UF_BOUNDS[c.uf]?.nome || c.uf;
                      return (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 8, background: '#10b98120', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <MapPinned size={14} style={{ color: '#10b981' }} />
                            </div>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#111', lineHeight: 1.1 }}>{ufName}</div>
                              <div style={{ fontSize: '.75rem', color: '#94a3b8', fontWeight: 600 }}>{c.uf}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: '.85rem', color: '#374151', marginBottom: 8, fontWeight: 600 }}>
                            {c.contagem.toLocaleString('pt-BR')} escolas
                          </div>
                          {ufInfo && (
                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '8px 10px', marginBottom: 8 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                <div><div style={{ fontSize: '.65rem', color: '#94a3b8', fontWeight: 600 }}>MUNICIPIOS</div><div style={{ fontWeight: 800, fontSize: '.95rem', color: '#111' }}>{ufInfo.total_municipios}</div></div>
                                <div><div style={{ fontSize: '.65rem', color: '#94a3b8', fontWeight: 600 }}>SCORE MEDIO</div><div style={{ fontWeight: 800, fontSize: '.95rem', color: ufInfo.score_medio >= 30 ? '#059669' : '#f59e0b' }}>{ufInfo.score_medio || '-'}</div></div>
                                <div><div style={{ fontSize: '.65rem', color: '#94a3b8', fontWeight: 600 }}>MUNICIPAIS</div><div style={{ fontWeight: 800, fontSize: '.95rem', color: '#10b981' }}>{ufInfo.escolas_municipais?.toLocaleString('pt-BR')}</div></div>
                                <div><div style={{ fontSize: '.65rem', color: '#94a3b8', fontWeight: 600 }}>POPULACAO</div><div style={{ fontWeight: 800, fontSize: '.95rem', color: '#111' }}>{(ufInfo.populacao_total / 1000000).toFixed(1)}M</div></div>
                              </div>
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            {[
                              { label: 'Municipal', val: c.municipal, c: '#10b981' },
                              { label: 'Estadual', val: c.estadual, c: '#8b5cf6' },
                              { label: 'Federal', val: c.federal, c: '#3b82f6' },
                              { label: 'Privada', val: c.privada, c: '#f59e0b' },
                            ].filter(d => d.val > 0).map(d => (
                              <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.c }} />
                                <span style={{ fontSize: '.78rem', fontWeight: 500 }}>{d.label}: {d.val.toLocaleString('pt-BR')}</span>
                              </div>
                            ))}
                          </div>
                          <button onClick={() => { handleSelectUf(c.uf!); }} style={{
                            width: '100%', marginTop: 10, padding: '8px', borderRadius: 8,
                            background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white',
                            border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.82rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          }}><Eye size={14} /> Ver Municipios</button>
                        </>
                      );
                    })() : currentZoom <= 12 && c.municipio ? (
                      /* ── Zoom Z6-12: Municipality-level popup ── */
                      <>
                        <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#111', marginBottom: 2, lineHeight: 1.2 }}>
                          {c.municipio}
                        </div>
                        <div style={{ fontSize: '.78rem', color: '#6b7280', marginBottom: 8 }}>
                          {c.contagem.toLocaleString('pt-BR')} escolas · {c.uf}
                        </div>
                        {intelDetail && intelDetail.nomeMunicipio?.toLowerCase() === c.municipio?.toLowerCase() ? (
                          <>
                            {intelDetail.gestao && (
                              <div style={{ background: '#eff6ff', borderRadius: 8, padding: '6px 10px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <UserCheck size={14} style={{ color: '#3b82f6', flexShrink: 0 }} />
                                <div>
                                  <div style={{ fontSize: '.7rem', color: '#3b82f6', fontWeight: 700 }}>PREFEITO</div>
                                  <div style={{ fontSize: '.82rem', fontWeight: 700, color: '#1e293b' }}>{intelDetail.gestao.prefeitoNome}</div>
                                  <div style={{ fontSize: '.7rem', color: '#64748b' }}>{intelDetail.gestao.prefeitoPartido} · 2025-2028</div>
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                              {intelDetail.financeiro?.faixaFinanceira && (
                                <div style={{ background: intelDetail.financeiro.faixaFinanceira === 'ALTA' ? '#f0fdf4' : intelDetail.financeiro.faixaFinanceira === 'MEDIA' ? '#fffbeb' : '#fef2f2', borderRadius: 8, padding: '5px 8px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 600 }}>FAIXA</div>
                                  <div style={{ fontWeight: 800, fontSize: '.85rem', color: intelDetail.financeiro.faixaFinanceira === 'ALTA' ? '#059669' : intelDetail.financeiro.faixaFinanceira === 'MEDIA' ? '#d97706' : '#dc2626' }}>{intelDetail.financeiro.faixaFinanceira}</div>
                                </div>
                              )}
                              {intelDetail.score && (
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '5px 8px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 600 }}>SCORE</div>
                                  <div style={{ fontWeight: 800, fontSize: '.85rem', color: intelDetail.score.scoreTotal >= 50 ? '#dc2626' : '#059669' }}>{intelDetail.score.scoreTotal}</div>
                                </div>
                              )}
                              {intelDetail.financeiro?.pibMunicipal && (
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '5px 8px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 600 }}>PIB</div>
                                  <div style={{ fontWeight: 800, fontSize: '.82rem', color: '#111' }}>R$ {(intelDetail.financeiro.pibMunicipal / 1000000).toFixed(0)}M</div>
                                </div>
                              )}
                              {intelDetail.educacao && (
                                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '5px 8px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 600 }}>MUNICIPAIS</div>
                                  <div style={{ fontWeight: 800, fontSize: '.85rem', color: '#10b981' }}>{intelDetail.educacao.qtdEscolasMunicipais}</div>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                            {[
                              { label: 'Municipal', val: c.municipal, c: '#10b981' },
                              { label: 'Estadual', val: c.estadual, c: '#8b5cf6' },
                              { label: 'Federal', val: c.federal, c: '#3b82f6' },
                              { label: 'Privada', val: c.privada, c: '#f59e0b' },
                            ].filter(d => d.val > 0).map(d => (
                              <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.c }} />
                                <span style={{ fontSize: '.78rem', fontWeight: 500 }}>{d.label}: {d.val.toLocaleString('pt-BR')}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <ActionButton c={c} clusterMetric={clusterMetric} currentZoom={currentZoom} />
                      </>
                    ) : (
                      /* ── Generic/Regional cluster popup (Z6-11 with UF but no municipio) ── */
                      <>
                        <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 2, color: '#111' }}>
                          {c.uf ? (UF_BOUNDS[c.uf]?.nome || c.uf) : (c.municipio || 'Cluster')}
                        </div>
                        {c.uf && (
                          <div style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>{c.uf}</div>
                        )}
                        <div style={{ fontSize: '.85rem', color: '#374151', marginBottom: 8, fontWeight: 600 }}>
                          {c.contagem.toLocaleString('pt-BR')} escolas
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                          {[
                            { label: 'Municipal', val: c.municipal, c: '#10b981' },
                            { label: 'Estadual', val: c.estadual, c: '#8b5cf6' },
                            { label: 'Federal', val: c.federal, c: '#3b82f6' },
                            { label: 'Privada', val: c.privada, c: '#f59e0b' },
                          ].filter(d => d.val > 0).map(d => (
                            <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.c }} />
                              <span style={{ fontSize: '.78rem', fontWeight: 500 }}>{d.label}: {d.val.toLocaleString('pt-BR')}</span>
                            </div>
                          ))}
                        </div>
                        <ActionButton c={c} clusterMetric={clusterMetric} currentZoom={currentZoom} />
                      </>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          });
          })()}

          {/* ── Individual schools at high zoom ── */}
          {detalheEscolas
            .filter(ce => {
              if (['municipal', 'estadual', 'federal', 'privada'].includes(clusterMetric)) {
                return ce.dependenciaAdministrativa?.toLowerCase() === clusterMetric;
              }
              return true;
            })
            .map((ce) => {
            const depColors: Record<string, string> = { Municipal: '#10b981', Estadual: '#8b5cf6', Federal: '#3b82f6', Privada: '#f59e0b' };
            const color = depColors[ce.dependenciaAdministrativa] || '#94a3b8';
            return (
              <CircleMarker key={`det-${ce.id}`} center={[ce.latitude, ce.longitude]} radius={5}
                pathOptions={{ fillColor: color, fillOpacity: 0.8, color: 'white', weight: 1.5 }}>
                <Popup>
                  <div style={{ minWidth: 250, fontFamily: 'Inter, system-ui, sans-serif' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                      <span style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: 600 }}>INEP {ce.codigoInep}</span>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#111', lineHeight: 1.3, marginBottom: 3 }}>{ce.nome}</div>
                    <div style={{ fontSize: '.82rem', color: '#6b7280', marginBottom: 6 }}>{ce.municipio}</div>
                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: '.72rem', fontWeight: 600, background: `${color}15`, color }}>{ce.dependenciaAdministrativa}</span>
                    {ce.telefone && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                        <a href={`tel:${ce.telefone}`} style={{
                          flex: 1, padding: '7px', borderRadius: 8, fontSize: '.78rem', fontWeight: 700,
                          background: '#10b98115', color: '#059669', border: '1px solid #10b98130',
                          textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer',
                        }}><Phone size={12} /> {ce.telefone}</a>
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}

          {/* ── Our schools ── */}
          {escolas.filter((escola) => !filterDep || (escola.tipoRede || '').toLowerCase() === filterDep.toLowerCase()).map((escola) => {
            const idx = escolas.indexOf(escola);
            const pos = positions[idx];
            const color = REDE_COLORS[escola.tipoRede || 'MUNICIPAL'] || '#6b7280';
            const alunos = escola._count?.alunos || escola.totalAlunos || 0;
            const turmas = escola._count?.turmas || 0;
            return (
              <Marker key={escola.id} position={pos} icon={createSchoolIcon(color)} zIndexOffset={1000}>
                <Popup>
                  <div style={{ minWidth: 250, fontFamily: 'Inter, system-ui, sans-serif' }}>
                    <div style={{ padding: '1px 6px', borderRadius: 4, fontSize: '.56rem', fontWeight: 700, background: '#10b98120', color: '#10b981', display: 'inline-block', marginBottom: 5 }}>NOSSA ESCOLA</div>
                    <div style={{ fontWeight: 800, fontSize: '1rem', color: '#111', lineHeight: 1.3, marginBottom: 3 }}>{escola.nome}</div>
                    <div style={{ fontSize: '.75rem', color: '#6b7280', marginBottom: 10 }}>{escola.cidade}, {escola.uf} · {escola.codigo}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5, marginBottom: 10 }}>
                      <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '.55rem', color: '#6b7280', fontWeight: 600 }}>ALUNOS</div>
                        <div style={{ fontWeight: 800, color: '#059669', fontSize: '1rem' }}>{alunos.toLocaleString('pt-BR')}</div>
                      </div>
                      <div style={{ background: '#eff6ff', borderRadius: 8, padding: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '.55rem', color: '#6b7280', fontWeight: 600 }}>TURMAS</div>
                        <div style={{ fontWeight: 800, color: '#2563eb', fontSize: '1rem' }}>{turmas}</div>
                      </div>
                      <div style={{ background: `${color}10`, borderRadius: 8, padding: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '.55rem', color: '#6b7280', fontWeight: 600 }}>REDE</div>
                        <div style={{ fontWeight: 800, color, fontSize: '.68rem' }}>{escola.tipoRede}</div>
                      </div>
                    </div>
                    <button onClick={() => navigate(`/master/escolas/${escola.id}`)} style={{
                      width: '100%', padding: '8px', borderRadius: 8,
                      background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                      color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.78rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    }}>
                      <ExternalLink size={13} /> Ver Detalhes
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Loading overlay */}
        {loadingClusters && (
          <div style={{
            position: 'absolute', top: 12, left: 56, zIndex: 999,
            background: 'rgba(59,130,246,.9)', borderRadius: 8, padding: '5px 12px',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin .6s linear infinite' }} />
            <span style={{ fontSize: '.7rem', fontWeight: 600, color: 'white' }}>Carregando...</span>
          </div>
        )}

        {/* Info overlay */}
        <div style={{
          position: 'absolute', top: 12, left: 56, zIndex: loadingClusters ? 998 : 999,
          background: 'rgba(255,255,255,.9)', backdropFilter: 'blur(8px)',
          borderRadius: 10, padding: '8px 14px', fontSize: '.85rem',
          boxShadow: '0 2px 8px rgba(0,0,0,.08)', border: '1px solid #e5e7eb',
          display: loadingClusters ? 'none' : 'flex', alignItems: 'center', gap: 8,
        }}>
          <Eye size={14} style={{ color: '#3b82f6' }} />
          <span style={{ fontWeight: 700, color: '#1e293b' }}>
            {currentZoom >= 12
              ? `${detalheEscolas.length} escolas`
              : currentZoom <= 5 ? `${filteredClusters.length} estados` : `${filteredClusters.length} municípios`
            }
            {filterDep && <span style={{ fontWeight: 600, color: DEP_COLORS[filterDep.toLowerCase()] || '#6b7280', marginLeft: 4 }}>({filterDep})</span>}
          </span>
          <span style={{ color: '#94a3b8', fontSize: '.72rem', fontWeight: 500 }}>
            · {currentZoom <= 5 ? 'Nacional' : currentZoom <= 7 ? 'Regional' : currentZoom <= 11 ? 'Municipal' : 'Operacional'}
            {clusterMetric !== 'escolas' && ` · Exibindo: ${clusterMetric}`}
          </span>
        </div>

        {/* Legend */}
        <div style={{
          position: 'absolute', bottom: 12, right: 12, zIndex: 999,
          background: 'rgba(255,255,255,.92)', backdropFilter: 'blur(8px)',
          borderRadius: 12, padding: '12px 16px', fontSize: '.78rem',
          boxShadow: '0 2px 12px rgba(0,0,0,.1)', border: '1px solid #e5e7eb',
          minWidth: 150,
        }}>
          <div style={{ fontWeight: 800, marginBottom: 8, fontSize: '.85rem' }}>Legenda</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#f97316', border: '2px solid #ea580c' }} />
              <span style={{ fontWeight: 700, fontSize: '.78rem', color: '#111' }}>Tem Nossas Escolas</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#475569', border: '2px solid #334155' }} />
              <span style={{ fontWeight: 600, fontSize: '.78rem', color: '#6b7280' }}>Sem Nossas Escolas</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 6 }}>
            <div style={{ fontSize: '.68rem', color: '#94a3b8', fontWeight: 700, marginBottom: 4, letterSpacing: '.04em' }}>ESCOLAS INDIVIDUAIS</div>
            {[
              { d: 'Municipal', c: '#10b981' }, { d: 'Estadual', c: '#8b5cf6' },
              { d: 'Federal', c: '#3b82f6' }, { d: 'Privada', c: '#f59e0b' },
            ].map(({ d, c }) => (
              <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: c }} /> <span style={{ fontWeight: 500 }}>{d}</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#10b981', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
              <span style={{ fontWeight: 700, fontSize: '.78rem' }}>Nossas Escolas</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes progressPulse { 0%, 100% { width: 30%; } 50% { width: 85%; } }
        .leaflet-popup-content-wrapper { border-radius: 14px !important; box-shadow: 0 8px 32px rgba(0,0,0,.15) !important; }
        .leaflet-popup-content { margin: 12px 14px !important; }
        .leaflet-popup-tip { box-shadow: none !important; }
        .cluster-label { background: none !important; border: none !important; box-shadow: none !important; padding: 0 !important; }
        .cluster-label::before { display: none !important; }
      `}</style>
    </div>
  );
}
